import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { AppInfo } from '../../../global/environment';
import { OrbitUtils } from '../../../global/orbit/orbit-utils';
import { SimpleProgressBarComponent } from '../../../shared/components/simple-progress-bar/simple-progress-bar.component';
import { SkeletonLoaderComponent } from '../../../shared/components/skeleton-loader/skeleton-loader.component';
import { ORBIT_BUNDLE, ORBIT_BUNDLE_SESSION_KEY } from '../constants';
import { GAService } from './services/ga-tags.service';
import { OrbitFormClaimService } from './services/orbit-form-claim.service';

@Component({
  selector: 'app-demo',
  imports: [RouterOutlet, SkeletonLoaderComponent, CommonModule, SimpleProgressBarComponent],
  templateUrl: './orbit-claim.component.html',
  styleUrl: './orbit-claim.component.scss',
})
export class OrbitClaimComponent implements OnInit {
  planNumber = signal<string>('');

  steps = ['info', 'repair-details', 'confirm-your-details', 'service-options', 'payment', 'book'];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly orbitClaimService: OrbitFormClaimService,
    private readonly router: Router,
    private readonly gaService: GAService
  ) {}

  private viewModel = computed(() => {
    const currentStep = this.orbitClaimService.currentStep()!;

    const currentPercentage = this.calculatePercentageThroughForm(currentStep);

    const isGameReplacement = this.orbitClaimService.isGameReplacement;
    const isService = this.orbitClaimService.state.claimData?.bundle?.claimType === 'SERVICE';

    const bookingType = isService ? 'Book a service' : 'Book a repair';
    const title = isGameReplacement ? 'Book a replacement' : bookingType;

    const latestStep = this.orbitClaimService.state.latestStep;
    return {
      isLoading: this.orbitClaimService.state.isLoading,
      mmrPageLink: `${AppInfo.Environment.claimsURL}/my-plans/${this.planNumber()}/claims`,
      percentage: parseFloat(currentPercentage),
      title,
      showPaymentTaken:
        (!!this.orbitClaimService.storedValue?.['payment'] && currentStep !== 'payment') ||
        (latestStep?.paymentData?.length && !latestStep?.paymentRequired),
    };
  });

  get vm() {
    return this.viewModel();
  }

  ngOnInit(): void {
    this.planNumber.set(this.route.snapshot.params['planNumber']);
    const { new_session } = this.route.snapshot.queryParams;

    OrbitUtils.initialise(this.router, this.planNumber(), this.orbitClaimService);

    if (new_session) {
      this.orbitClaimService.removeCachedItems();
      const parsedSessionData = JSON.parse(new_session);
      this.orbitClaimService.pendingSessionClaimType.set(parsedSessionData.claimType);
      this.orbitClaimService.createBundle(parsedSessionData).subscribe({
        next: () => this.pushStartClaimPageView(),
      });
      return;
    }
    const bundleKey = sessionStorage.getItem(ORBIT_BUNDLE_SESSION_KEY);
    const existingBundleValue = sessionStorage.getItem(ORBIT_BUNDLE);

    if (existingBundleValue && !this.orbitClaimService.state.claimData && bundleKey) {
      this.orbitClaimService.loadBundle(bundleKey, false);
    }
  }

  private pushStartClaimPageView(): void {
    const basePayload = {
      ...this.gaService.PAGE_VIEW(),
    };
    const openRepair = (this.orbitClaimService.state.claimData?.bundle as any)?.openRepair;
    const payload = {
      ...basePayload,
      pagename: 'claims-what-happened',
      productName: (basePayload.productName ?? '').toLowerCase(),
      repairer: openRepair?.repairer ?? '',
      repairTimeline: openRepair?.isClaimsTrackerEnabled ?? '',
      claimStatus: openRepair?.claimStatus ?? '',
    };

    this.gaService.sendGATag(payload);
  }

  calculatePercentageThroughForm(step: string) {
    const getIndex = (array: string[], key: string = '') => {
      let index = array.findIndex((i) => key.startsWith(i));

      if (index === -1) return 0;
      return index;
    };

    const currentStepIndex = getIndex(this.steps, step);

    const percentageSplitForStep = parseFloat(`${100 / this.steps.length}`);

    const currentStepsToGoThrough = this.orbitClaimService.state.claimData?.stepsToGoThrough ?? [];
    const percentagePerSubStep = parseFloat(`${percentageSplitForStep / (currentStepsToGoThrough.length || 1)}`);
    const currentPositionInSubStep = getIndex(currentStepsToGoThrough, step);
    const currentPercentage = parseFloat(
      `${percentageSplitForStep * currentStepIndex + percentagePerSubStep * currentPositionInSubStep}`
    ).toFixed(2);

    return currentPercentage;
  }
}
